import type { SortingState } from '@tanstack/react-table';
import type { PaginationState } from '@/components';
import {
  getProfileFilterSearchParams,
  getProfileFilterSlice,
} from '@/feature/patients/components/patient-list/profileFilter';
import { toIsoDateString } from '@/utils/date';
import type {
  AnonymousRecordUploadStatus,
  EfaxProviderSearch,
  EfaxSearchCriteria,
  EfaxSearchState,
  EfaxSortColumn,
  SearchAnonymousRecordFaxesInput,
} from '../types';
import {
  getOutputCategoryFilterSearchParams,
  getOutputCategoryFilterSlice,
} from './outputCategoryFilter';

const optionalString = (value: string) => {
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
};

const toProviderSearch = (
  providerName: string,
  facilityName: string,
  providerFacilityType: string,
): EfaxProviderSearch | undefined => {
  const provider: EfaxProviderSearch = {
    providerName: optionalString(providerName),
    facilityName: optionalString(facilityName),
    providerFacilityType: optionalString(providerFacilityType),
  };

  const hasValue = Object.values(provider).some((value) => value !== undefined);
  return hasValue ? provider : undefined;
};

export const mapEfaxSearchInput = (
  search: EfaxSearchState,
  sorting: SortingState,
  pagination: PaginationState,
): SearchAnonymousRecordFaxesInput => {
  const criteria: EfaxSearchCriteria = {
    documentName: optionalString(search.documentName),
    documentId: optionalString(search.documentId),
    receivedAtStart: search.receivedAtStart,
    receivedAtEnd: search.receivedAtEnd,
    status: optionalString(search.documentStatus) as
      | AnonymousRecordUploadStatus
      | undefined,
    ...getProfileFilterSearchParams(getProfileFilterSlice(search)),
    ...getOutputCategoryFilterSearchParams(
      getOutputCategoryFilterSlice(search),
    ),
    outputFileName: optionalString(search.outputFileName),
    patientName: optionalString(search.patientName),
    patientDateOfBirth: toIsoDateString(search.patientDob),
    requestReferenceId: optionalString(search.requestReferenceId),
    patientKey: optionalString(search.patientKey),
    sendingProvider: toProviderSearch(
      search.providerName,
      search.providerFacilityName,
      search.providerType,
    ),
    receivingProvider: toProviderSearch(
      search.receivingProviderName,
      search.receivingProviderFacilityName,
      search.receivingProviderType,
    ),
  };

  return {
    search: criteria,
    pagination: {
      offset: pagination.page * pagination.pageSize,
      limit: pagination.pageSize,
    },
    sortColumns: sorting.map(({ id, desc }) => ({
      column: id as EfaxSortColumn,
      order: desc ? 'desc' : 'asc',
    })),
  };
};
